<template>
  <div id="app" class="app">
    <template>
      <Titlebar
        :content="title"
        :display-right="hasTopMenuButtons"
        :background-color="titlebarBackgroundColor"
        @leftInvoke="closePage"
        @rightInvoke="showTopMenu(true)"
      ></Titlebar>
      <div class="app-body">
        <PayPage
          v-if="pageStatus[0]"
          ref="paypage"
          :key="isMemberCode ? 'user-code' : 'pay-code'"
          :qr-code-info="qrCodeInfo"
          :is-member-code="isMemberCode"
          :is-meituan-pay-scene="isMeituanPayScene"
          :biz-logo="bizLogo"
          :background-image="backgroundImage"
          :brightness="brightness"
          :offline-info="offlineInfo"
          :get-user-qr-code-timer="getUserQrCodeTimer"
          :query-loop="queryLoop"
          :only-group="onlyGroup"
          :status-bar-background-color="statusBarBackgroundColor"
        ></PayPage>
        <BindCard
          v-else-if="pageStatus[1]"
          :qr-code-info="qrCodeInfo"
        ></BindCard>
        <ErrorPage
          v-else-if="pageStatus[2]"
          :fail-code="failCode"
          :error-msg="qrCodeInfo.pageActionMsg"
          @getUserQRCode="getInfo"
        ></ErrorPage>
      </div>
      <!-- 支付结果页面覆盖层（独立状态，终态页面） -->
      <PaySuccess
        v-if="showPayResult"
        :pay-result-info="payResultInfo"
        class="pay-result-overlay"
      ></PaySuccess>
    </template>
    <div v-if="maskShow" class="mask" @click="closeMask()"></div>
    <transition name="topmenu">
      <TopMenu
        v-if="topMenuShow"
        :menu-list="qrCodeInfo.pageTopButtons"
        :brightness="brightness"
      ></TopMenu>
    </transition>
  </div>
</template>

<script>
import Titlebar from './components/titlebar'
import PayPage from './components/index'
import ErrorPage from './components/error'
import BindCard from './components/bindCard'
import TopMenu from './components/topmenu'
import PaySuccess from './components/paySuccess'
import { getUrlParam } from '@mx/acquiring-utils'
import { AESEncrypt, getLocation, getSystem } from './utils/util'
import env from './utils/env';
import { init, fetch_seed } from '../src/assets/unionpaycode_rust'

import {
  syncBarCodeOffline,
  updateOfflineSeed,
  str2Hex,
  hex2Str,
  sendRaptorPoint,
  sendRaptorErr,
  paramsInfo,
} from './utils/util'
import { pagePVMap } from './utils/lxMap'
import { pageTopButtons, pageBottomButtons } from './utils/preset'

/**
 * MSI 实例缓存（组件外部，全局共享）
 *
 * 【鸿蒙系统特殊处理】
 * 鸿蒙系统的 KNB 能力与 iOS/Android 不同，部分功能需要使用 MSI 桥
 * MSI 使用 KNB 提供的桥接层（@mtfe/msi-knb-loader）
 *
 * 【为什么需要缓存】
 * - MSI 需要动态导入和初始化（msi.load()）
 * - 初始化过程较慢，需要缓存实例避免重复加载
 * - 全局共享，所有组件实例共用同一个 MSI 实例
 */
let msiInstance = null
let msiLoadPromise = null

export default {
  name: 'App',
  components: {
    Titlebar,
    PayPage,
    BindCard,
    TopMenu,
    ErrorPage,
    PaySuccess,
  },
  data() {
    return {
      qrCodeInfo: {
        userBankcardList: [],
        qrCode: '',
        pageActionType: '',
        querySteps: [1, 1, 1],
        pageBottomButtons: [],
        pageTopButtons: [],
      },
      maskShow: false,
      topMenuShow: false,
      pageStatus: [false, false, false], // 0-付款码页面，1-绑卡页面，2-异常页面
      payToken: '',
      showPayResult: false, // 独立控制支付结果页显示
      payResultInfo: {}, // 支付结果信息
      getUserQrCodeTimer: 0,
      brightness: 0.8,
      type: getUrlParam('type'),
      failCode: 100000,
      offlineInfo: {},
      ttiSent: false,
      onlyGroup: true, // 是否仅在团app上展示，适配商企通
      queryLoop: 1,
      isMemberCode: false, // 默认为付款码模式
      bizLogo: getUrlParam('bizLogo') ? decodeURIComponent(getUrlParam('bizLogo')) : '', // 会员码logo
      backgroundImage: getUrlParam('backgroundImage') ? decodeURIComponent(getUrlParam('backgroundImage')) : '', // 背景图
      disableOffline: false, // 是否禁用离线码（仅禁用离线码，不影响其他功能）
      isMeituanPayScene: false, // 是否为美团支付场景（非银联场景）
                                 // 临时方案：基于 entry=maicai_member 判断，后期会移到后端决策
      statusBarBackgroundColor: '#FFC300', // 状态栏背景色，根据 entry 参数动态设置
      titlebarBackgroundColor: 'transparent', // 导航栏背景色，默认透明
    }
  },
  computed: {
    title() {
      if (this.type === 'scan') {
        return '向商家付款'
      }
      return this.qrCodeInfo.pageTitle
    },
    // 判断是否有顶部菜单按钮
    hasTopMenuButtons() {
      return !!(this.qrCodeInfo.pageTopButtons && this.qrCodeInfo.pageTopButtons.length > 0)
    },
  },
  created() {
    // 从URL参数中读取entry字段并更新公共参数
    const urlEntry = getUrlParam('entry')
    if (urlEntry) {
      paramsInfo.entry = urlEntry
    }

    // 根据entry设置状态栏背景色，entry不为空时显示白色，否则为默认的黄色
    this.statusBarBackgroundColor = urlEntry && urlEntry !== '' ? '#FFFFFF' : '#FFC300'

    this.qrCodeInfo.pageTopButtons = pageTopButtons
    this.qrCodeInfo.pageBottomButtons = pageBottomButtons
    this.onlyGroup = !getUrlParam('bizIdentityId')

    // 整体禁用离线码
    this.disableOffline = true
    // 买菜会员场景：美团支付场景（非银联场景），不显示银联标提示，并禁用离线码
    // TODO: 临时方案，根据 entry 判断场景类型，后期会移到后端决策
    if (urlEntry === 'maicai_member') {
      this.isMeituanPayScene = true
      console.log('✅ [MeituanPayScene] 买菜会员场景，美团支付场景（非银联，不显示银联标，禁用离线码）')
    }

    this.queryLoop = this.onlyGroup
      ? this.qrCodeInfo.querySteps && this.qrCodeInfo.querySteps[0]
      : 1
  },
  mounted() {
    this.getInfo()

    // 获取并记录屏幕亮度
    this.$bridge.getBrightness().then((res) => {
      this.brightness = res
    })
    // 设置WebView属性
    this.setWebViewStatus()

    // 设置背景图
    this.setBackgroundImage()

    // 事件监听器
    this.$eb.$on('getOnlineQrCode', this.getOnlineQrCode)
    this.$eb.$on('showTopMenu', this.showTopMenu)
  },
  beforeDestroy() {
    this.$bridge.unsubscribeAll()
    this.$eb.$off('getOnlineQrCode')
    this.$eb.$off('showTopMenu')
    window.removeEventListener('resize', this.resizeQrCode)
  },
  methods: {
    // 根据接口返回的codeType处理会员码/付款码模式
    handleCodeTypeFromAPI(apiResponse) {
      const { codeType, guideList } = apiResponse

      console.log('🎯 [CodeType] 接口返回的codeType:', codeType)
      console.log('📋 [GuideList] 接口返回的guideList:', guideList)

      if (codeType === 'USER') {
        // 会员码模式
        this.isMemberCode = true
        console.log('✅ [Mode] 切换到会员码模式')
      } else if (codeType === 'PAY') {
        // 付款码模式
        this.isMemberCode = false
        console.log('✅ [Mode] 切换到付款码模式')
      } else {
        // 默认付款码模式（兼容旧接口）
        this.isMemberCode = false
        console.log('✅ [Mode] 默认付款码模式 (codeType未定义或为其他值)')
      }
    },
    // getInfo() {
    //   if (this.type === 'scan') {
    //     // 获取主扫的绑卡引导信息
    //     this.getBindGuide()
    //     this.$bridge.subAppear(() => {
    //       this.$bridge.getStorage('pay-bindcard-success', (res) => {
    //         if (res.value === '1') {
    //           this.$bridge.closePage()
    //         }
    //       })
    //     })
    //   } else {
    //     // 仅在团app上的页面使用离线能力
    //     if (this.onlyGroup) {
    //       // 首次拉取，WASM离线-->Native离线-->在线
    //       this.getOfflineQrCodeByWASM(true)
    //         .then(() => {
    //           this.getOnlineQrCode(true)
    //         })
    //         .catch(() => {
    //           this.getOfflineQrCodeByNative()
    //             .then(() => {
    //               this.getOnlineQrCode(true)
    //             })
    //             .catch((e) => {
    //               this.getOnlineQrCode(true)
    //             })
    //         })
    //       return
    //     }
    //     // 针对商企通外投场景，独立验密后返回时先从url内获取payToken
    //     this.payToken = getUrlParam('payToken')
    //     this.getOnlineQrCode(true)
    //   }
    // },
    getInfo() {
      // 判断类型是否为 主扫
      if (this.type === 'scan') {
        // 处理 主扫 类型的情况
        this.handleScanType()
      } else {
        // 处理非 主扫 类型的情况
        this.handleNonScanType()
      }
    },

    handleScanType() {
      // 获取主扫的绑卡引导信息
      this.getBindGuide()
      // 监听页面出现事件
      this.$bridge.subAppear(() => {
        // 获取本地存储中的支付绑卡成功标识
        this.$bridge.getStorage('pay-bindcard-success', (res) => {
          // 如果支付绑卡成功，则关闭页面
          if (res.value === '1') {
            this.$bridge.closePage()
          }
        })
      })
    },

    handleNonScanType() {
      // 判断是否仅在团app上展示
      if (this.onlyGroup) {
        // 处理仅在团app上展示的情况
        this.handleOnlyGroup()
        return
      }
      // 针对商企通外投场景，独立验密后返回时先从url内获取payToken
      // 获取URL中的payToken参数
      this.payToken = getUrlParam('payToken')
      // 获取在线二维码
      this.getOnlineQrCode(true)
    },

    handleOnlyGroup() {
      // 如果禁用离线码，直接获取在线码
      if (this.disableOffline) {
        console.log('🚫 [Offline] 已禁用离线码，跳过离线码获取，直接获取在线码')
        this.getOnlineQrCode(true)
        return
      }

      // 使用WASM获取离线二维码
      this.getOfflineQrCodeByWASM(true)
        .then(() => {
          // 获取在线二维码
          this.getOnlineQrCode(true)
        })
        .catch(() => {
          // 使用Native获取离线二维码
          this.getOfflineQrCodeByNative()
            .then(() => {
              // 获取在线二维码
              this.getOnlineQrCode(true)
            })
            .catch((e) => {
              // 获取在线二维码
              this.getOnlineQrCode(true)
            })
        })
    },
    // 获取在线二维码
    async getOnlineQrCode(isFirstTime = false) {
      // 🔒 如果正在显示支付结果页（终态），不执行更新
      if (this.showPayResult) {
        console.log('⚠️ [getOnlineQrCode] 正在显示支付结果页，忽略更新请求')
        return
      }

      // 记录开始时间
      const start = Date.now()
      let location

      // 尝试获取地理位置信息
      try {
        location = await getLocation()
      } catch (e) {
        location = e
      }

      // 获取URL参数
      const urlParams = getUrlParam()
      const {
        bizIdentityId,
        bizOrderId,
        payType,
        payTypeName,
        payTypeTail,
      } = urlParams

      // 请求在线二维码
      try {
        // 构建请求数据对象
        const requestData = {
          payToken: this.payToken,
          topPaytypeId: isFirstTime ? getUrlParam('topPaytypeId') : undefined,
          location,
          bizIdentityId,
          bizOrderId,
          payType,
          payTypeName: payTypeName
            ? decodeURIComponent(payTypeName)
            : payTypeName,
          payTypeTail: payTypeTail
            ? decodeURIComponent(payTypeTail)
            : payTypeTail,
        }

        // 发送请求获取在线二维码
        const res = await this.$ajax({
          url: '/qrcode/app/merchantscan/userqrcode',
          data: requestData,
        })

        // 显示二维码
        env.isTitans && window.KNB.use('eh.show')

        // 首次进入，离线成功，且请求在线码时后端请求上游异常，则不作处理，继续展示离线码
        // 新增：非商企通的情况下
        if (
          this.qrCodeInfo.pageActionType === 110 &&
          res.pageActionType === undefined &&
          this.onlyGroup
        ) {
          return
        }
        // 🟩 【新增】检测codeType是否变化，如果变化则刷新页面
        // 🟩 【修改】不再使用 window.location.reload()。codeType 变化时直接赋值响应式数据，
        // 交给 handleCodeTypeFromAPI 更新 isMemberCode，再配合模板中 PayPage 的 :key="isMemberCode"
        // 在模式切换时强制重建子组件树，等效于局部 reload，但不影响 App 层的 payToken 与原生桥监听
        if (res.codeType && this.qrCodeInfo.codeType && this.qrCodeInfo.codeType !== res.codeType) {
          console.log('🔄 [CodeType Changed] 从', this.qrCodeInfo.codeType, '变为', res.codeType, '，局部重建 PayPage')
        }

        this.qrCodeInfo = res

        // 🟩 【新增】根据接口返回的codeType判断会员码/付款码模式
        this.handleCodeTypeFromAPI(res)

        // 首次进入页面，发送性能监控数据
        if (
          isFirstTime &&
          !this.ttiSent &&
          this.qrCodeInfo.pageActionType === 0
        ) {
          sendRaptorPoint(20, Date.now() - start)
          sendRaptorPoint(29, window.performance.now())
          this.ttiSent = true
        }

        // 判断用户交互动作以及展示页面
        this.judgeAction()

        // 更新离线种子
        if (
          this.qrCodeInfo.offlineQrCodeManager === 1 &&
          'WebAssembly' in window
        ) {
          localStorage.setItem('unionT1', Math.round(Date.now() / 1000))
          try {
            const seedRes = await this.$ajax({ url: '/qrcode/offline/getseed' })
            console.log('H5 getSeed success')
            localStorage.setItem('unionQrCodeSeed', seedRes.encrypt_res)
            localStorage.setItem('unionT3', Math.round(Date.now() / 1000))
          } catch (e) {}
        }
        updateOfflineSeed(this.qrCodeInfo.offlineQrCodeManager)
      } catch (err) {
        // 获取在线二维码失败的处理
        console.warn('获取在线二维码失败', err)
        env.isTitans && window.KNB.use('eh.show')

        // 首次进入页面，在线码失败后直接展示error页面
        if (isFirstTime) {
          this.failCode = err.code || 100000

          if (this.qrCodeInfo.pageActionType === 110) {
            return
          }
          this.showPage(2)
          return
        }

        // 获取用户银联二维码失败，展示重试页面
        if (err.code === 300110) {
          this.failCode = err.code
          this.showPage(2)
          return
        }

        // 获取二维码失败+无网走离线方案
        // 商企通禁用离线二维码
        // 买菜会员场景禁用离线二维码
        if ([300301, 100000].includes(err.code) && this.onlyGroup && !this.disableOffline) {
          try {
            await this.getOfflineQrCodeByWASM()
            this.bindEvent()
          } catch (e) {
            console.warn('通过WASM获取离线二维码失败', e)
            try {
              await this.getOfflineQrCodeByNative()
              this.bindEvent()
            } catch (e) {
              console.warn('通过Native获取离线二维码失败', e)
              this.qrCodeInfo.pageActionMsg = ''
              this.failCode = err.code
              this.showPage(2)
            }
          }
          return
        }

        // 其余情况，展示重试页面
        this.failCode = err.code
        this.showPage(2)
      }
    },
    // 桥模式获取离线二维码
    getOfflineQrCodeByNative() {
      const start = Date.now()
      // 获取本地缓存银行卡列表信息
      return syncBarCodeOffline(1)
        .then((res) => {
          this.qrCodeInfo.userBankcardList = res.bankCardInfo
          this.qrCodeInfo.userBankcardList[0].isDefault = true
          this.qrCodeInfo.userBankcardList.forEach((p) => {
            p.isEnable = true
            p.isSign = true
          })
          const cacheBankId = this.qrCodeInfo?.userBankcardList[0]?.bankcardId
          // 获取离线二维码code
          return syncBarCodeOffline(2, {
            bankCardID: cacheBankId,
          })
            .then((res) => {
              console.log(`NATIVE_SUCCESS：${Date.now() - start}ms`)
              const metricInst = window.Owl && window.Owl.MetricManager()
              metricInst &&
                metricInst.setTags({
                  type: 'init',
                })
              metricInst &&
                metricInst.setMetric('native_success', Date.now() - start)
              env.isTitans && window.KNB.use('eh.show')
              this.qrCodeInfo.qrCode = res.payCode
              this.qrCodeInfo.qrCodeId = 0
              // 自定义离线场景
              this.qrCodeInfo.pageActionType = 110
              this.judgeAction()
            })
            .catch((e) => {
              throw e
            })
        })
        .catch((e) => {
          throw e
        })
    },
    // WASM模式获取离线二维码
    getOfflineQrCodeByWASM(isFirstTime = false) {
      return new Promise(async (resolve, reject) => {
        // 浏览器不支持WASM，直接return
        if (!'WebAssembly' in window) {
          sendRaptorErr('不支持WASM', {}, 'customError')
          reject()
          return
        }
        let runStart
        try {
          if (window.GeneratePayCodeModule == null) {
            // 等待WASM模块初始化完成后再进行方法调用
            const initStart = Date.now()
            console.time('WASM_INIT')
            await init()
            console.timeEnd('WASM_INIT')
            const metricInst = window.Owl && window.Owl.MetricManager()
            metricInst &&
              metricInst.setMetric('wasm_init', Date.now() - initStart)
            window.GeneratePayCodeModule = fetch_seed
          }
          runStart = Date.now()
          const LSSeedInfo = localStorage.getItem('unionQrCodeSeed')
          // 没有离线种子信息，直接return
          if (!LSSeedInfo) {
            reject()
            return
          }
          const AESKey = localStorage.getItem('AESkey')
          const unionT1 = localStorage.getItem('unionT1') || 0
          const unionT3 = localStorage.getItem('unionT3') || 0
          const decryptData = AESEncrypt('decrypt', {
            key: AESKey,
            data: LSSeedInfo,
          })
          this.offlineInfo = JSON.parse(decryptData.data)
          console.log(`【离线信息】：${JSON.stringify(this.offlineInfo)}`)
          const { verifyPin: vd, unionpayTime, bankcardList } = this.offlineInfo
          // 处理银行卡中文乱码
          this.qrCodeInfo.userBankcardList = JSON.parse(
            JSON.stringify(bankcardList)
          )
          this.qrCodeInfo.userBankcardList.forEach((p) => {
            const hex = str2Hex(p.bankcardName)
            const name = hex2Str(hex)
            p.bankcardName = name
          })
          // 无可用银行卡，直接return
          if (!this.qrCodeInfo.userBankcardList[0]) {
            reject()
            return
          }
          const {
            seed,
            rate,
            fpeKey,
            sessionKey: sk,
            tweak,
          } = this.qrCodeInfo.userBankcardList[0]
          const time = String(
            Math.round(Date.now() / 1000) +
              unionpayTime -
              Math.round((unionT3 - unionT1) / 2) -
              unionT1
          )
          if (!seed || !vd || !fpeKey || !sk || !tweak || !time || !rate) {
            reject()
            return
          }
          console.log(
            `【离线计算参数】：${seed}|${vd}|${fpeKey}|${sk}|${tweak}|${time}|${rate}`
          )
          const qrCode = window.GeneratePayCodeModule(
            seed,
            vd,
            fpeKey,
            sk,
            tweak,
            time,
            String(rate)
          )
          const runTime = Date.now() - runStart
          console.log(`【离线码】：耗时${runTime}ms。${qrCode}`)
          if (isFirstTime && !this.ttiSent) {
            const metricInst = window.Owl && window.Owl.MetricManager()
            metricInst &&
              metricInst.setTags({
                type: 'init',
              })
            metricInst && metricInst.setMetric('wasm_success', runTime)
            sendRaptorPoint(20, runTime)
            sendRaptorPoint(29, window.performance.now())
            this.ttiSent = true
          }
          this.qrCodeInfo.userBankcardList[0].isDefault = true
          this.qrCodeInfo.userBankcardList.forEach((p) => {
            p.isEnable = true
            p.isSign = true
          })
          env.isTitans && window.KNB.use('eh.show')
          this.qrCodeInfo.qrCode = qrCode
          this.qrCodeInfo.qrCodeId = 0
          // 自定义离线场景
          this.qrCodeInfo.pageActionType = 110
          this.judgeAction()
          resolve()
        } catch (e) {
          console.log(
            `【离线码生成失败】：耗时${Date.now() - runStart}ms。${e}`
          )
          // sendRaptorErr('WASM生码失败', e, 'customError')
          reject(e)
        }
      })
    },
    // 关闭页面
    closePage() {
      if (this.qrCodeInfo.pageActionType === 0) {
        this.$lx.closePageMC({}, { cid: pagePVMap['pay'] })
      } else if (this.qrCodeInfo.pageActionType === 3 || this.type === 'scan') {
        this.$lx.closePageMC({}, { cid: pagePVMap['bind'] })
      } else {
        this.$lx.closePageMC({}, { cid: pagePVMap['error'] })
      }
      // 恢复屏幕亮度
      this.$bridge.setBrightness(this.brightness)
        .finally(() => {
          console.warn('closepage finally')
          this.$bridge.closePage();
        })
    },
    // 操作右上角菜单
    showTopMenu(flag, option) {
      // 如果要打开菜单，但是没有菜单项，则不执行
      if (flag && !this.hasTopMenuButtons) {
        console.warn('⚠️ [TopMenu] 没有菜单项，不打开菜单')
        return
      }

      if (this.qrCodeInfo.pageActionType === 0) {
        this.$lx.topMenuMC({}, { cid: pagePVMap['pay'] })
        if (option) {
          this.$lx.topMenuContentMC(
            { button_name: option.title },
            {
              cid:
                this.qrCodeInfo.pageActionType === -2
                  ? pagePVMap['error']
                  : pagePVMap['pay'],
            }
          )
        }
      } else {
        this.$lx.topMenuMC({}, { cid: pagePVMap['error'] })
        if (option) {
          this.$lx.topMenuContentMC(
            { button_name: option.title },
            { cid: pagePVMap['error'] }
          )
        }
      }
      this.maskShow = flag
      this.topMenuShow = flag
    },
    // 跳转独立验证
    jumpToVerify(scene) {
      const baseUrl =
        process.env.VUE_APP_VERIFY_URL ||
        'https://npay.meituan.com/resource/pass-verify/index.html?merchantNo=11000004918439&partnerId=114'
      // 商企通模式
      if (!this.onlyGroup) {
        this.$bridge.jumpPage(
          `${baseUrl}&successUrl=${encodeURIComponent(
            location.href
          )}&scene=${scene}`,
          {}
        )
        return
      }
      this.$bridge.openPage(`${baseUrl}&closeWebview=1&scene=${scene}`)
    },
    closeMask() {
      // 待完善场景，不允许用户点击蒙层关闭半弹窗
      if (this.qrCodeInfo.pageActionType === 4) {
        return
      }
      this.showTopMenu(false)
    },
    bindEvent() {
      this.$bridge.setBrightness(0.8)
      this.$bridge.subAppear(this.refreshCode)
      this.$bridge.subDisappear(this.resetBrightness)
      this.$bridge.subForeground(this.refreshCode)
      this.$bridge.subBackground(this.resetBrightness)

      // 根据系统类型设置截录屏限制
      const system = getSystem()
      if (system === 'android') {
        // Android：使用 KNB 业务桥防止截屏
        syncBarCodeOffline(9).catch(() => {})
      } else if (system === 'harmony') {
        // 鸿蒙：使用 MSI 基础桥设置截录屏限制
        this.setHarmonyScreenCaptureProtection()
      }

      // iOS：监听截屏事件，截屏后提示并刷新二维码
      window.addEventListener('KNB:screenCaptured', this.screenCapture)
      window.addEventListener('resize', this.resizeQrCode)
      // 根据后端下发的qrCodeLife刷新码，默认60s
      clearInterval(this.getUserQrCodeTimer)
      const refreshInterval = this.qrCodeInfo.qrCodeLife ? this.qrCodeInfo.qrCodeLife * 1000 : 60000
      this.getUserQrCodeTimer = setInterval(() => {
        this.getOnlineQrCode()
      }, refreshInterval)
    },
    resizeQrCode() {
      this.$eb.$emit('resizeQrCode')
    },
    refreshCode() {
      this.getOnlineQrCode()
      this.$bridge.setBrightness(0.8)
    },
    resetBrightness() {
      this.$bridge.setBrightness(this.brightness)
      window.removeEventListener('KNB:screenCaptured', this.screenCapture)
    },
    screenCapture() {
      this.$modal.show({
        title: '安全提示',
        subTitle: '为了保证资金安全，请不要将您的二维码分享给其他人',
        btnContent: '知道了',
        btnFunction: this.screenShotAction,
      })
      this.showTopMenu(false)
      this.$eb.$emit('closePreview', true)
    },
    // 独立验证场景：监听页面视图出现事件，读取内存内payToken并重新获取二维码
    bindVerifyKNBEvent() {
      this.$bridge.subAppear(() => {
        this.$bridge.getStorage(
          'payToken',
          (result) => {
            if (!result || !result.value) {
              this.$bridge.closePage()
              return
            }
            this.payToken = result.value
            this.getOnlineQrCode()
          },
          () => {
            this.$bridge.closePage()
          }
        )
      })
    },
    setWebViewStatus() {
      // 隐藏自带导航栏
      this.$bridge.setNavigationBarHidden(1)
      // 禁止反弹效果
      this.$bridge.setBouncesEnabled(0)
      // 设置状态栏样式
      this.$bridge.setStatusBarStyle(0, this.statusBarBackgroundColor)
    },
    getBindGuide() {
      this.$ajax({
        url: '/qrcode/account/bindGuide',
        data: {
          entry: 'payment_code', //主扫绑卡entry
        },
      })
        .then((res) => {
          env.isTitans && window.KNB.use('eh.show')
          this.loading = false
          this.qrCodeInfo.bindGuide = res
          this.showPage(1)
        })
        .catch((err) => {
          this.loading = false
          this.showPage(2)
        })
    },
    // 判断页面行为
    judgeAction() {
      switch (this.qrCodeInfo.pageActionType) {
        case -2:
          // 展示通用错误提示文案（文案内容是同级返回字段pageActionMsg）
          this.failCode = 111111
          this.showPage(2)
          break
        case -1:
          // 已弃用：启用i版（跳转至iEntranceUrl）
          break
        case 0:
          // 显示被扫首页（已开通 & 已绑有效卡），客户端不做任何动作。
          this.showPage(0)
          this.bindEvent()
          break
        case 110:
          // 自定义离线场景，同样进入展码页
          this.showPage(0)
          this.bindEvent()
          break
        case 1:
          this.bindVerifyKNBEvent()
          // 开户验密
          this.jumpToVerify(616)
          break
        case 2:
          this.bindVerifyKNBEvent()
          // 风控拦截验密
          this.jumpToVerify(204)
          break
        case 3:
          // 引导用户绑卡 （未绑卡 | 绑了不支持的卡）
          this.showPage(1)
          this.$bridge.subAppear(this.refreshCode)
          break
        case 4:
          // 引导用户转签 （绑卡信息待完善）
          this.showPage(0)
          this.$bridge.subAppear(this.refreshCode)
          break
        default:
          this.showPage(2)
          break
      }
    },
    showPage(index) {
      // 0-付款码页面，1-绑卡页面，2-异常页面
      const newPageStatus = this.pageStatus.map((p, _index) => {
        return index === _index
      })
      this.pageStatus = newPageStatus
    },
    // 显示支付成功页面（终态页面，独立状态）
    showPaySuccessPage(payResultInfo) {
      this.payResultInfo = payResultInfo
      this.titlebarBackgroundColor = '#FFFFFF'
      this.showPayResult = true
    },
    // 返回付款码页面（关闭支付结果页）
    backToPayPage() {
      // 关闭支付结果页（终态页面）
      this.showPayResult = false

      // 确保显示付款码页面
      this.showPage(0)

      // 恢复导航栏样式：透明背景
      this.titlebarBackgroundColor = 'transparent'

      // 🔒 主动触发二维码更新
      this.$eb.$emit('getOnlineQrCode')
    },
    screenShotAction() {
      this.$modal.hide()
      this.getOnlineQrCode()
    },
    // 鸿蒙系统设置截录屏限制
    async setHarmonyScreenCaptureProtection() {
      try {
        console.log('[HarmonyOS] 开始设置截录屏限制')

        // 情况1：如果已经加载过，直接使用缓存
        if (msiInstance) {
          console.log('[HarmonyOS] 使用缓存的 MSI 实例')
          msiInstance.setVisualEffectOnCapture({
            visualEffect: 'hidden', // hidden 表示隐藏内容，none 表示不隐藏
            success: (res) => {
              console.log('[HarmonyOS] 截录屏限制设置成功:', res)
            },
            fail: (err) => {
              console.error('[HarmonyOS] 截录屏限制设置失败:', err)
            }
          })
          return
        }

        // 情况2：如果正在加载中，等待加载完成
        if (msiLoadPromise) {
          console.log('[HarmonyOS] MSI 正在加载中，等待...')
          const msi = await msiLoadPromise
          if (msi) {
            msi.setVisualEffectOnCapture({
              visualEffect: 'hidden',
              success: (res) => {
                console.log('[HarmonyOS] 截录屏限制设置成功:', res)
              },
              fail: (err) => {
                console.error('[HarmonyOS] 截录屏限制设置失败:', err)
              }
            })
          }
          return
        }

        // 首次加载 MSI
        console.log('[HarmonyOS] 首次加载 MSI 库...')
        msiLoadPromise = (async () => {
          try {
            const { msi } = await import('@mtfe/msi-knb-loader')
            const loadResult = await msi.load()

            if (loadResult) {
              msiInstance = msi
              console.log('[HarmonyOS] MSI 初始化成功')
              return msi
            } else {
              console.error('[HarmonyOS] MSI 初始化失败')
              return null
            }
          } catch (error) {
            console.error('[HarmonyOS] MSI 加载失败:', error)
            return null
          }
        })()

        const msi = await msiLoadPromise
        if (!msi) {
          console.error('[HarmonyOS] MSI 不可用')
          return
        }

        console.log('[HarmonyOS] 调用 setVisualEffectOnCapture')

        // 直接调用 msi.setVisualEffectOnCapture
        msi.setVisualEffectOnCapture({
          visualEffect: 'hidden', // hidden 表示隐藏内容，none 表示不隐藏
          success: (res) => {
            console.log('[HarmonyOS] 截录屏限制设置成功:', res)
          },
          fail: (err) => {
            console.error('[HarmonyOS] 截录屏限制设置失败:', err)
          }
        })
      } catch (error) {
        sendRaptorErr('msi截录屏限制设置异常', error, 'customError')
      }
    },
    // 设置背景图
    setBackgroundImage() {
      const appElement = document.getElementById('app')
      if (this.backgroundImage) {
        // 如果URL参数中有背景图,使用URL中的背景图
        appElement.style.background = `url('${this.backgroundImage}') no-repeat center center`
        appElement.style.backgroundSize = 'cover'
      } else {
        // 否则使用黄色背景
        appElement.style.background = '#ffc300'
      }
    },
  },
}
</script>

<style lang="less">
@import './assets/app.less';
@import './assets/variable.less';

.element::-webkit-scrollbar {
  width: 0 !important;
}
.app {
  &-body {
    height: 100%;
    margin-top: 64px;
    margin-top: calc(44px + constant(safe-area-inset-top));
    margin-top: calc(44px + env(safe-area-inset-top));
  }
}
.mask {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: #000000;
  opacity: 0.76;
  z-index: 12;
}
.topmenu-enter-active {
  animation: topmenu-slide-in 0.3s;
}
.topmenu-leave-active {
  animation: topmenu-slide-out 0.3s;
}

// 支付结果页面覆盖层样式
.pay-result-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;  // 提高到 1001，高于二维码预览的 1000
  background: #ffffff;
  overflow-y: auto;
  // 覆盖导航栏
  padding-top: 64px;
  padding-top: calc(44px + constant(safe-area-inset-top));
  padding-top: calc(44px + env(safe-area-inset-top));
}
</style>
